import { useState, useEffect } from 'react';
import { getRootWorkspaceDetails, type WorkspaceUsage } from '@/api/group';
import { currentUserIsAdminOfRootGroup, formatBytes } from '../../util/UtilFunctions';
import { getPackageType, getSubscriptionStaticInfo } from '../../util/subscriptionDetails';
import { SectionHeader } from './SectionHeader';

interface CompanyInfoSectionProps {
  currentUserInfo: Record<string, unknown>;
  groupsList: unknown[] | null;
  rootWorkspaceDetails: Record<string, unknown> | null;
  updateRootWorkspaceDetails: (details: unknown) => void;
  currentUserCreatorGroup: Record<string, unknown>;
}

export function CompanyInfoSection({
  currentUserInfo,
  groupsList,
  rootWorkspaceDetails,
  updateRootWorkspaceDetails,
  currentUserCreatorGroup,
}: CompanyInfoSectionProps) {
  const showStorage = currentUserIsAdminOfRootGroup(currentUserInfo, groupsList);
  const packageType = getPackageType(currentUserInfo, groupsList);

  const companyTitle = (currentUserCreatorGroup.title as Record<string, string>)?.S ?? '';

  return (
    <div className="my-4">
      <SectionHeader>Company information</SectionHeader>
      <div className="max-w-[700px]">
        <InfoRow label="Owner company" value={companyTitle} />
        <InfoRow label="Package type" value={packageType} />
        {showStorage && (
          <StorageDetails
            rootWorkspaceDetails={rootWorkspaceDetails}
            updateRootWorkspaceDetails={updateRootWorkspaceDetails}
            packageType={packageType}
          />
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="ml-1 flex items-center border-b border-border py-3">
      <h5 className="text-base font-normal">{label}</h5>
      <span className="ml-auto text-muted-foreground">{value}</span>
    </div>
  );
}

// ── Storage Details ─────────────────────────────────────────────────

interface StorageDetailsProps {
  rootWorkspaceDetails: Record<string, unknown> | null;
  updateRootWorkspaceDetails: (details: unknown) => void;
  packageType: string;
}

function StorageDetails({
  rootWorkspaceDetails,
  updateRootWorkspaceDetails,
  packageType,
}: StorageDetailsProps) {
  const [usage, setUsage] = useState<WorkspaceUsage | null>(null);

  useEffect(() => {
    if (rootWorkspaceDetails) {
      setUsage(rootWorkspaceDetails as unknown as WorkspaceUsage);
      return;
    }
    getRootWorkspaceDetails().then((data) => {
      setUsage(data);
      updateRootWorkspaceDetails(data);
    }).catch(() => {
      // Failed to load usage — non-critical
    });
  }, [rootWorkspaceDetails, updateRootWorkspaceDetails]);

  const subscriptionConstants = getSubscriptionStaticInfo(packageType);
  const maxStorage = subscriptionConstants ? formatBytes(subscriptionConstants.maxStorageSpace) : '–';
  const maxTransfer = subscriptionConstants ? formatBytes(subscriptionConstants.dataTransfer.limit) : '–';

  const usedBytes = usage?.usedBytes?.N;
  const thisMonth = usage?.sentBytes?.thisMonth?.N;
  const lastMonth = usage?.sentBytes?.lastMonth?.N;

  return (
    <>
      <InfoRow label="Data transfer this month" value={`${formatBytes(thisMonth)} / ${maxTransfer}`} />
      <InfoRow label="Data transfer last month" value={`${formatBytes(lastMonth)} / ${maxTransfer}`} />
      <InfoRow label="Storage space" value={`${formatBytes(usedBytes)} / ${maxStorage}`} />
    </>
  );
}
